import { useMemo } from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import Animated from 'react-native-reanimated';
import { useCountUp } from '../../hooks/useCountUp';
import { useStaggeredEntrance } from '../../hooks/useStaggeredEntrance';
import { colors } from '../../theme/colors';
import { shadows } from '../../theme/shadows';
import { tokens } from '../../theme/tokens';
import { darkPrecisionTypography } from '../../theme/typography';
import { ShimmerSkeleton } from '../animations/Skeleton';
import { Card } from './Card';
import { Text } from './Text';

interface MetricCardProps {
  label: string;
  value: number | null | undefined;
  subtext?: string;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  index?: number;
  loading?: boolean;
  active?: boolean;
  style?: ViewStyle;
}

function formatNumber(value: number, decimals: number): string {
  return value.toFixed(decimals).replaceAll(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export function MetricCard({
  label,
  value,
  subtext,
  prefix = '',
  suffix = '',
  decimals = 0,
  index = 0,
  loading = false,
  active = false,
  style,
}: Readonly<MetricCardProps>) {
  const target = typeof value === 'number' ? value : 0;
  const animated = useCountUp(target, { duration: 900, easing: 'expo' });
  const { style: entranceStyle } = useStaggeredEntrance(index);

  const isComingSoon = value === null || value === undefined;
  const displayValue = useMemo(
    () => `${prefix}${formatNumber(animated, decimals)}${suffix}`,
    [animated, decimals, prefix, suffix],
  );

  const activeStyle: ViewStyle = active
    ? (Platform.select({
        web: { ...(shadows.glowHalo as ViewStyle) },
        default: shadows.glowHaloSoft as ViewStyle,
      }) as ViewStyle)
    : {};

  return (
    <Animated.View style={[entranceStyle, style]}>
      <Card variant={active ? 'accent' : 'default'} padding="lg" radius="lg" style={activeStyle}>
        <View style={styles.row}>
          <Text style={darkPrecisionTypography.label}>{label}</Text>
        </View>
        {isComingSoon || loading ? (
          <ShimmerSkeleton width="60%" height={32} style={styles.skeleton} />
        ) : (
          <Text style={[darkPrecisionTypography.metric, styles.value]} numberOfLines={1}>
            {displayValue}
          </Text>
        )}
        {subtext && !isComingSoon && (
          <Text style={[darkPrecisionTypography.metricSm, styles.subtext]}>{subtext}</Text>
        )}
      </Card>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.sm,
  },
  value: {
    color: colors.text.primary,
    marginTop: tokens.spacing.xs,
  },
  subtext: {
    marginTop: tokens.spacing.xs,
  },
  skeleton: {
    marginTop: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
  },
});
